/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * API for managing meeting bots, calendar integrations, and webhooks
 * OpenAPI spec version: 2.0.0
 */
import { z as zod } from "zod"

/**
 * Create a meet workspace — the parent resource that holds your SAML cert + private key for one Google Workspace's SSO config.

    A meet workspace represents one Google Workspace whose Legacy SSO profile points at our `/v2/meet-sso/*` endpoints. The cert and key on the workspace are shared by every `meet_login` (Workspace user identity) you attach to it, mirroring how Google Workspace stores a single verification certificate per SSO profile.

    **Two creation paths, same response shape:**

    - **Server-generated keypair** — pass `generate_keypair: true`. The server creates a self-signed RSA-2048 keypair with 10-year validity. Use this if you don't already have a SAML cert and want the simplest setup.
    - **Bring-your-own keypair** — pass `cert_pem` and `private_key_pem` together. Use this if you want to manage your own crypto or already have a cert/key pair you trust.

    Mutually exclusive: provide either `generate_keypair: true` OR (`cert_pem` + `private_key_pem`), not both.

    **Response always includes `cert_pem`** so you can upload it to Google's Legacy SSO profile in your Workspace admin console. `private_key_pem` is never returned.

    **After creation, you must:**
    1. Upload the returned `cert_pem` to Google Admin Console → Security → Set up SSO with third-party IdP → Legacy SSO profile.
    2. Set Sign-in URL to `https://api.meetingbaas.com/v2/meet-sso/sign-in` and Sign-out URL to `https://api.meetingbaas.com/v2/meet-sso/sign-out` in the same SSO profile. Enable "Use a domain-specific issuer" and assign the SSO profile to all users.
    3. Create one or more Workspace users that bots will sign in as, complete the "Welcome to Workspace" interactive login for each, and set language to "English (United States)".
    4. Add `meet_logins` (one per Workspace user) referencing this `workspace_id`.
    5. Optionally call `POST /v2/meet-workspaces/:workspace_id/verify` to run pre-flight checks.

    **Security:** The cert and key are encrypted at rest using AES-256-GCM. `private_key_pem` is never echoed in any response — including subsequent GETs. If you need it back, you must rotate via PATCH.

    **Per-team uniqueness:** Each `domain` may exist at most once per team. Attempting to create a duplicate returns 409.

    **Error Scenarios:**
    - `409 Conflict`: A workspace for this `domain` already exists.
    - `422 Unprocessable Entity`: Invalid cert/key (parse failure or modulus mismatch); both `generate_keypair` and `cert_pem` provided; neither provided.
 * @summary Create a meet workspace
 */
export const createMeetWorkspaceBodyNameDefault = "Google Workspace"
export const createMeetWorkspaceBodyNameMax = 100
export const createMeetWorkspaceBodyDomainMax = 253

export const createMeetWorkspaceBodyDomainRegExp = /^[a-z0-9.-]+\.[a-z]{2,}$/

export const createMeetWorkspaceBody = zod.object({
  name: zod
    .string()
    .min(1)
    .max(createMeetWorkspaceBodyNameMax)
    .default(createMeetWorkspaceBodyNameDefault)
    .describe(
      "User-friendly label for the workspace. Not unique — names can repeat within a team."
    ),
  domain: zod
    .string()
    .min(1)
    .max(createMeetWorkspaceBodyDomainMax)
    .regex(createMeetWorkspaceBodyDomainRegExp)
    .describe(
      "Google Workspace domain (or subdomain) where the Legacy SSO profile is configured. Must be a domain you own with the SAML SSO endpoints pointing at our /v2/meet-sso/* URLs."
    ),
  generate_keypair: zod
    .boolean()
    .optional()
    .describe(
      "Set to true to have the server generate a self-signed keypair. Mutually exclusive with cert_pem/private_key_pem."
    ),
  cert_pem: zod.string().min(1).optional().describe("PEM-encoded X.509 certificate."),
  private_key_pem: zod
    .string()
    .min(1)
    .optional()
    .describe("PEM-encoded RSA private key matching cert_pem. Never echoed back in any response."),
  extra: zod
    .record(zod.string(), zod.any())
    .describe(
      "An optional free-form JSON object you can attach to the workspace.\n\nStored as-is and never interpreted by the API. Useful for tagging workspaces with your own identifiers (e.g. internal tenant ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
    )
    .or(zod.null())
    .optional()
})

/**
 * List all meet workspaces for your team.

    Returns each workspace's metadata including `workspace_id`, `name`, `domain`, `state`, the `cert_pem` (so you can re-upload to Google admin if needed), and any recent failure context. `private_key_pem` is never included.

    **State Field:**
    - `active`: workspace is healthy; bots assigned to logins under this workspace will sign in successfully.
    - `invalid`: the system auto-disabled the workspace because Google rejected our SAML assertion (typically a cert mismatch). Every login under an invalid workspace is blocked from being assigned. Re-upload the correct cert to Google admin and use PATCH to set `state` back to `active`.

    Use this endpoint to inventory your SAML SSO setup or build a dashboard listing workspaces by health.
 * @summary List meet workspaces
 */
export const listMeetWorkspacesQueryExtraDefault = null

export const listMeetWorkspacesQueryParams = zod.object({
  extra: zod
    .string()
    .describe(
      'Filter workspaces by matching values in the `extra` JSON payload.\n\nFormat: `\"field:value\"` for single condition or `\"f1:v1,f2:v2\"` for multiple (AND).\nValues are exact-match (case-sensitive). Workspaces without the field are excluded.'
    )
    .or(zod.null())
    .optional()
})

export const listMeetWorkspacesResponseDataItemWorkspaceIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const listMeetWorkspacesResponse = zod.object({
  success: zod.literal(true),
  data: zod.array(
    zod.object({
      workspace_id: zod
        .string()
        .uuid()
        .regex(listMeetWorkspacesResponseDataItemWorkspaceIdRegExp)
        .describe("The unique identifier (UUID) of the meet workspace"),
      name: zod.string(),
      domain: zod.string(),
      cert_pem: zod.string(),
      state: zod
        .enum(["active", "invalid"])
        .describe(
          "The current state of the meet workspace.\n\n- `active`: SAML cert is healthy and bots can sign in\n- `invalid`: System auto-flipped this on a SAML rejection. Manual re-enable required."
        ),
      failure_data: zod.any().optional(),
      last_error_message: zod.string().or(zod.null()),
      last_error_at: zod.string().or(zod.null()),
      extra: zod
        .record(zod.string(), zod.any())
        .describe(
          "An optional free-form JSON object you can attach to the workspace.\n\nStored as-is and never interpreted by the API. Useful for tagging workspaces with your own identifiers (e.g. internal tenant ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
        )
        .or(zod.null()),
      created_at: zod.string(),
      updated_at: zod.string()
    })
  )
})

/**
 * Get full details for a single meet workspace.

    Returns the same fields as the list endpoint, scoped to one workspace. Useful for confirming the cert that was uploaded to Google matches the one we have on file (compare `cert_pem` against the Verification Certificate in your Google Admin Console).

    **Failure Context:** If `state` is `invalid`, the response includes `last_error_message`, `last_error_at`, and a structured `failure_data` object describing the bot run that triggered the auto-disable. Use this to investigate before re-enabling.

    Returns 404 if the workspace is not found or does not belong to your team.
 * @summary Get a meet workspace
 */
export const getMeetWorkspacePathWorkspaceIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const getMeetWorkspaceParams = zod.object({
  workspace_id: zod
    .string()
    .uuid()
    .regex(getMeetWorkspacePathWorkspaceIdRegExp)
    .describe("The unique identifier (UUID) of the meet workspace")
})

export const getMeetWorkspaceResponseDataWorkspaceIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const getMeetWorkspaceResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    workspace_id: zod
      .string()
      .uuid()
      .regex(getMeetWorkspaceResponseDataWorkspaceIdRegExp)
      .describe("The unique identifier (UUID) of the meet workspace"),
    name: zod.string(),
    domain: zod.string(),
    cert_pem: zod.string(),
    state: zod
      .enum(["active", "invalid"])
      .describe(
        "The current state of the meet workspace.\n\n- `active`: SAML cert is healthy and bots can sign in\n- `invalid`: System auto-flipped this on a SAML rejection. Manual re-enable required."
      ),
    failure_data: zod.any().optional(),
    last_error_message: zod.string().or(zod.null()),
    last_error_at: zod.string().or(zod.null()),
    extra: zod
      .record(zod.string(), zod.any())
      .describe(
        "An optional free-form JSON object you can attach to the workspace.\n\nStored as-is and never interpreted by the API. Useful for tagging workspaces with your own identifiers (e.g. internal tenant ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
      )
      .or(zod.null()),
    created_at: zod.string(),
    updated_at: zod.string()
  })
})

/**
 * Update a meet workspace — rename it, rotate its keypair, or re-enable it after auto-disable.

    **Rename:** pass only `name`.

    **Rotate cert/key:** pass both `cert_pem` and `private_key_pem` together. The new pair takes effect immediately for all logins under this workspace. You must upload the new cert to Google Admin Console at the same time — there is a brief window where the cert in our system and the cert in Google admin can diverge, during which bot sign-ins fail. Coordinate the swap.

    **Re-enable after auto-disable:** pass `state: "active"`. Only valid if the workspace is currently `invalid`. This clears `failure_data`, `last_error_message`, and `last_error_at` atomically. Only do this after fixing the underlying issue (e.g., re-uploading the correct cert to Google admin).

    `domain` is immutable. To change the domain, delete the workspace (after draining its logins) and create a new one.

    **State Restrictions:** PATCH cannot set `state` to `invalid` — that's system-only. Returns 400 if you try.

    **Error Scenarios:**
    - `400 Bad Request`: Attempted to set `state` to a value other than `active`.
    - `404 Not Found`: Workspace not found or does not belong to your team.
    - `422 Unprocessable Entity`: Cert/key parse failure or mismatched pair; `cert_pem` provided without `private_key_pem` (or vice versa).
 * @summary Update a meet workspace
 */
export const updateMeetWorkspacePathWorkspaceIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const updateMeetWorkspaceParams = zod.object({
  workspace_id: zod
    .string()
    .uuid()
    .regex(updateMeetWorkspacePathWorkspaceIdRegExp)
    .describe("The unique identifier (UUID) of the meet workspace")
})

export const updateMeetWorkspaceBodyNameMax = 100

export const updateMeetWorkspaceBody = zod.object({
  name: zod
    .string()
    .min(1)
    .max(updateMeetWorkspaceBodyNameMax)
    .optional()
    .describe(
      "User-friendly label for the workspace. Not unique — names can repeat within a team."
    ),
  cert_pem: zod.string().min(1).optional().describe("PEM-encoded X.509 certificate."),
  private_key_pem: zod
    .string()
    .min(1)
    .optional()
    .describe("PEM-encoded RSA private key matching cert_pem. Never echoed back in any response."),
  state: zod
    .enum(["active"])
    .optional()
    .describe(
      "Only 'active' is allowed via PATCH (system-only sets 'invalid'). Use this to re-enable a workspace after fixing the underlying failure."
    ),
  extra: zod
    .record(zod.string(), zod.any())
    .describe(
      "An optional free-form JSON object you can attach to the workspace.\n\nStored as-is and never interpreted by the API. Useful for tagging workspaces with your own identifiers (e.g. internal tenant ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
    )
    .or(zod.null())
    .optional()
})

export const updateMeetWorkspaceResponseDataWorkspaceIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const updateMeetWorkspaceResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    workspace_id: zod
      .string()
      .uuid()
      .regex(updateMeetWorkspaceResponseDataWorkspaceIdRegExp)
      .describe("The unique identifier (UUID) of the meet workspace"),
    name: zod.string(),
    domain: zod.string(),
    cert_pem: zod.string(),
    state: zod
      .enum(["active", "invalid"])
      .describe(
        "The current state of the meet workspace.\n\n- `active`: SAML cert is healthy and bots can sign in\n- `invalid`: System auto-flipped this on a SAML rejection. Manual re-enable required."
      ),
    failure_data: zod.any().optional(),
    last_error_message: zod.string().or(zod.null()),
    last_error_at: zod.string().or(zod.null()),
    extra: zod
      .record(zod.string(), zod.any())
      .describe(
        "An optional free-form JSON object you can attach to the workspace.\n\nStored as-is and never interpreted by the API. Useful for tagging workspaces with your own identifiers (e.g. internal tenant ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
      )
      .or(zod.null()),
    created_at: zod.string(),
    updated_at: zod.string()
  })
})

/**
 * Delete a meet workspace.

    **This cascades to every `meet_login` under the workspace.** All login rows are removed from the database in the same transaction; any bots currently assigned to those logins will have their `assigned_meet_login_id` set to `null` (the row deletion does not stop in-flight bots, but their post-bot accounting will reference a now-deleted login).

    **In-Use Guard:** If any login under this workspace has an active SSO session in flight (`active_session_count > 0`), the request returns 409 instead of deleting. Wait for the bots to finish or stop them first via the bots API. The aggregate count across all child logins is shown in the error message.

    **Operational Note:** This is a hard delete. Any reference to deleted login `credential_id` values from old bot records becomes a dangling reference. The cascade is irreversible — re-creating a workspace gives you a new `workspace_id` even if you reuse the same domain.

    Returns 404 if the workspace is not found or does not belong to your team.
 * @summary Delete a meet workspace (cascades to its logins)
 */
export const deleteMeetWorkspacePathWorkspaceIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const deleteMeetWorkspaceParams = zod.object({
  workspace_id: zod
    .string()
    .uuid()
    .regex(deleteMeetWorkspacePathWorkspaceIdRegExp)
    .describe("The unique identifier (UUID) of the meet workspace")
})

export const deleteMeetWorkspaceResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    message: zod.string()
  })
})
